import { useState } from "react";
import PropTypes from "prop-types";
// @mui
import { Button, Checkbox, FormGroup, Grid, Typography } from "@mui/material";
// components
import { Controller, useForm } from "react-hook-form";
import {
  anredeArray,
  bezahltBisArray,
  defaultSiteFormValues,
  fullMemberFormData,
  getNewMemberPayedTillValue,
  gradArray,
} from "../../../components/form/fields/inputFields";
import TextField from "../../../components/form/fields/TextField";
import SelectField from "../../../components/form/fields/SelectField";
import DEDateField from "../../../components/form/fields/DEDateField";
import BeltAvatar from "../../../components/beltavatar/BeltAvatar";
import FileUpload from "../../../components/fileUpload/FileUpload";
import { getDateFromDateField } from "../../../utils/dataUtils";

// ----------------------------------------------------------------------

export default function NewMemberForm({
  memberData,
  siteData,
  handleMemberDataSubmit,
  lockedFields,
}) {
  const {
    memberIdentifier,
    latestMemberNumber,
    memberContribution,
    enrollmentFee,
    contractDuration,
    contractExtension,
    noticePeriod,
  } = siteData;
  const [showUpload, setShowUpload] = useState(false);

  const { handleSubmit, control, watch, setError } = useForm({
    defaultValues: {
      memberNumber: memberIdentifier + latestMemberNumber,
      contribution: memberContribution,
      contractDuration,
      contractExtension,
      noticePeriod,
      registrationFee: enrollmentFee,
      title: "",
      degree: 0,
      beginningDate: new Date(),
      birthdate: new Date(),
      contractDate: new Date(),
      lastTesting: new Date(),
      payRegistrationFee: false,
      comment: "",
      payedTill: getNewMemberPayedTillValue(),
      ...memberData,
    },
  });

  const degree = watch("degree");

  const handleShowUploadContract = () => {
    setShowUpload(!showUpload);
  };

  const onSubmit = (data) => {
    // Check if registration fee should be collected, if true value must be provided!
    if (data.payRegistrationFee && !data.registrationFee) {
      setError("registrationFee", {
        type: "custom",
        message: "Bitte Anmeldegebühr eingeben.",
      });
      return null;
    }

    handleMemberDataSubmit({
      ...data,
      birthdate: getDateFromDateField(data?.birthdate),
      contractDate: getDateFromDateField(data?.contractDate),
      beginningDate: getDateFromDateField(data?.beginningDate),
      lastTesting: getDateFromDateField(data?.lastTesting),
    });
  };

  const renderSiteInputFields = (fields) => {
    return fields.map((field) => {
      const isLocked = lockedFields?.includes(field.value);
      if (field.type === "date") {
        return (
          <Grid
            key={field.value + "-field"}
            item
            xs={12}
            md={field.size ? field.size : 4}
          >
            <DEDateField
              control={control}
              name={field.value}
              label={field.label}
              required={field.required}
              errorMsg={field.errorMsg}
              helperText={field.helperText}
              disabled={isLocked}
            />
          </Grid>
        );
      }

      return (
        <Grid
          key={field.value + "-field"}
          item
          xs={12}
          md={field.size ? field.size : 4}
        >
          <TextField
            control={control}
            name={field.value}
            label={field.label}
            type={field.type}
            variant="outlined"
            required={field.required}
            errorMsg={field.errorMsg}
            helperText={field.helperText}
            fullWidth
            multiline={field?.multiline}
            endAdornment={field?.endAdornment}
            disabled={isLocked}
          />
        </Grid>
      );
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ paddingTop: 5 }}
        >
          <Grid item xs={12}>
            <Typography variant="h6">Mitgliedsdaten</Typography>
          </Grid>
          <Grid item xs={12}>
            <SelectField
              style={{ minWidth: 180 }}
              control={control}
              name="title"
              value="title"
              label="Anrede"
              variant="outlined"
              required={true}
              itemArray={anredeArray}
            />
          </Grid>
          {/* {renderSiteInputFields(fullMemberFormData)} */}
          {renderSiteInputFields(fullMemberFormData.slice(1, 4))}

          <Grid item xs={12}>
            <Typography variant="h6">Vertragsdaten</Typography>
          </Grid>
          {renderSiteInputFields(fullMemberFormData.slice(4, 10))}
          <Grid item xs={12} md={4}>
            <SelectField
              style={{ minWidth: 180 }}
              control={control}
              name="payedTill"
              value="payedTill"
              label="Bezahlt bis"
              variant="outlined"
              required
              itemArray={bezahltBisArray}
              helperText="Bis welchem Monat wurde der Beitrag bereits gezahlt?"
              disabled={lockedFields?.includes("payedTill")}
            />
          </Grid>
          {renderSiteInputFields(fullMemberFormData.slice(10, 11))}
          <Grid item style={{ padding: 20 }} xs={12} md={4}>
            <FormGroup row>
              <Controller
                name="payRegistrationFee"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                    disabled={lockedFields?.includes("payRegistrationFee")}
                  />
                )}
              />
              <label
                style={{
                  color: "black",
                  marginLeft: 0,
                  marginTop: "8px",
                  fontWeight: 500,
                  fontSize: "1.1rem",
                }}
              >
                Anmeldegebühr einmalig einziehen?
              </label>
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6">Zusatz</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectField
              style={{ minWidth: 180 }}
              control={control}
              name="degree"
              value="degree"
              label="Gürtelgrad"
              variant="outlined"
              required
              itemArray={gradArray}
              disabled={lockedFields?.includes("degree")}
            />
          </Grid>
          <Grid alignContent={"center"} item xs={12} md={2}>
            <BeltAvatar beltNumber={degree} size="medium" />
          </Grid>
          {renderSiteInputFields(fullMemberFormData.slice(12, 13))}
          {renderSiteInputFields(fullMemberFormData.slice(13, 16))}
          <Grid item xs={12} md={6}>
            <Typography variant="h6">Vertrag</Typography>
            <Button
              onClick={handleShowUploadContract}
              variant="contained"
              color="info"
              sx={{
                mt: 1,
                mb: 1,
                fontSize: "125%",
              }}
            >
              Vertrag hochladen
            </Button>
            {showUpload && <FileUpload key={"Test1"} />}
          </Grid>
        </Grid>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          sx={{
            mt: 2,
            mb: 3,
            fontSize: "125%",
          }}
        >
          Weiter
        </Button>
      </form>
    </>
  );
}

NewMemberForm.propTypes = {
  siteData: PropTypes.object,
  dialogType: PropTypes.string,
  lockedFields: PropTypes.arrayOf(PropTypes.string),
};

NewMemberForm.defaultProps = {
  siteData: defaultSiteFormValues, // Hier den Standardwert festlegen
  lockedFields: [],
};
